import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { api, type EmailProspectStatus } from '@/lib/api';
import { Page } from '@/components/AppShell';
import { Badge, Button, Card, CardBody, EmptyState, ErrorState, Input, LoadingState, Select, Stat } from '@/components/ui/primitives';

const STATUSES: EmailProspectStatus[] = ['new', 'contacted', 'unsubscribed', 'bounced'];
const PAGE_SIZE = 100;

function statusVariant(status: EmailProspectStatus): 'success' | 'warning' | 'danger' | 'default' {
  if (status === 'new') return 'success';
  if (status === 'contacted') return 'default';
  return status === 'bounced' ? 'danger' : 'warning';
}

export function EmailListPage() {
  const queryClient = useQueryClient();
  const [q, setQ] = React.useState('');
  const [state, setState] = React.useState('');
  const [category, setCategory] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [offset, setOffset] = React.useState(0);
  const [exporting, setExporting] = React.useState(false);

  const filters = { q: q.trim() || undefined, state: state || undefined, category: category || undefined, status: (status || undefined) as EmailProspectStatus | undefined };
  const counts = useQuery({ queryKey: ['email-prospect-counts'], queryFn: api.emailProspectCounts, refetchInterval: 60_000 });
  const list = useQuery({
    queryKey: ['email-prospects', filters, offset],
    queryFn: () => api.emailProspects({ ...filters, limit: PAGE_SIZE, offset }),
    placeholderData: (previous) => previous,
  });
  const setProspectStatus = useMutation({
    mutationFn: (input: { id: number; status: EmailProspectStatus }) => api.setEmailProspectStatus(input.id, input.status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['email-prospects'] });
      void queryClient.invalidateQueries({ queryKey: ['email-prospect-counts'] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Could not update the status'),
  });

  const total = list.data?.total ?? 0;
  const exportList = async () => {
    setExporting(true);
    try {
      await api.exportEmailProspects(filters);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Page
      title="Email List"
      description="Businesses with an email but no phone we can call. Kept for later email marketing. They are not leads."
      actions={
        <Button variant="outline" onClick={exportList} loading={exporting}>
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      }
    >
      <div className="space-y-4 p-5 md:p-6">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat label="Total" value={counts.data?.total ?? '…'} />
          <Stat label="New" value={counts.data?.byStatus.new ?? '…'} />
          <Stat label="Contacted" value={counts.data?.byStatus.contacted ?? '…'} />
          <Stat label="Unsubscribed or bounced" value={counts.data ? counts.data.byStatus.unsubscribed + counts.data.byStatus.bounced : '…'} hint="Never exported" />
        </div>

        <Card>
          <CardBody className="flex flex-wrap items-end gap-3">
            <Input className="w-64" placeholder="Search name, email or city" value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} />
            <Select className="w-32" value={state} onChange={(e) => { setState(e.target.value); setOffset(0); }}>
              <option value="">All states</option>
              {counts.data?.byState.map((s) => <option key={s.state} value={s.state}>{s.state} ({s.count})</option>)}
            </Select>
            <Select className="w-56" value={category} onChange={(e) => { setCategory(e.target.value); setOffset(0); }}>
              <option value="">All categories</option>
              {counts.data?.byCategory.map((c) => <option key={c.category} value={c.category}>{c.category} ({c.count})</option>)}
            </Select>
            <Select className="w-40" value={status} onChange={(e) => { setStatus(e.target.value); setOffset(0); }}>
              <option value="">Any status</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </CardBody>
        </Card>

        {list.isLoading ? (
          <LoadingState />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : total === 0 ? (
          <EmptyState icon={Mail} title="No email prospects yet" description="The research engine adds businesses here when it finds an email address but no phone number." />
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/70 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2">Business</th>
                    <th className="px-4 py-2">Email</th>
                    <th className="px-4 py-2">Location</th>
                    <th className="px-4 py-2">Category</th>
                    <th className="px-4 py-2">Why it is here</th>
                    <th className="px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data?.rows.map((row) => (
                    <tr key={row.id} className="border-b border-border/40">
                      <td className="px-4 py-2 font-medium">{row.businessName}</td>
                      <td className="px-4 py-2 font-mono text-xs">{row.email}</td>
                      <td className="px-4 py-2">{[row.city, row.state].filter(Boolean).join(', ')}</td>
                      <td className="px-4 py-2">{row.category ?? '—'}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{row.reason}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <Badge variant={statusVariant(row.status)}>{row.status}</Badge>
                          <Select className="h-8 w-36" value={row.status} onChange={(e) => setProspectStatus.mutate({ id: row.id, status: e.target.value as EmailProspectStatus })}>
                            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                          </Select>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 text-xs text-muted-foreground">
              <span>{offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</Button>
                <Button variant="outline" size="sm" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</Button>
              </div>
            </div>
          </Card>
        )}
      </div>
    </Page>
  );
}
